import React from 'react';
import { Panel } from 'rsuite';
import { ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { MetricValue } from '@/types/primaryLedgerSnapshots';

interface MetricCardProps {
  label: string;
  value: MetricValue;
  formatValue?: (value: number) => string;
  prefix?: string;
  suffix?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  formatValue,
  prefix = '',
  suffix = ''
}) => {
  const defaultFormatter = (val: number) => val.toFixed(2);
  const formatter = formatValue || defaultFormatter;
  
  const formattedCurrent = `${prefix}${formatter(value.current)}${suffix}`;
  const formattedPrevious = value.previous !== undefined 
    ? `${prefix}${formatter(value.previous)}${suffix}`
    : null;
  
  const getTrendColor = () => {
    if (value.changeDirection === 'up') return 'text-green-600 dark:text-green-400';
    if (value.changeDirection === 'down') return 'text-red-600 dark:text-red-400';
    return 'text-gray-500 dark:text-gray-400';
  };
  
  const getTrendIcon = () => {
    const iconClass = "h-4 w-4";
    if (value.changeDirection === 'up') return <ArrowUp className={iconClass} />;
    if (value.changeDirection === 'down') return <ArrowDown className={iconClass} />;
    return <Minus className={iconClass} />;
  };
  
  const trendColor = getTrendColor();
  
  return (
    <Panel 
      bordered 
      className="bg-white dark:bg-gray-800 hover:shadow-md transition-shadow"
    >
      <div className="p-4">
        <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
          {label}
        </div>
        
        <div className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {formattedCurrent}
        </div>
        
        {value.changePercent !== undefined && (
          <div className={`flex items-center gap-1 text-sm ${trendColor}`}>
            {getTrendIcon()}
            <span className="font-medium">
              {Math.abs(value.changePercent).toFixed(1)}%
            </span>
          </div>
        )}
        
        {formattedPrevious && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Previous: {formattedPrevious}
          </div>
        )}
      </div>
    </Panel>
  );
};

export default MetricCard;